import { window, exists, document } from "browser-monads"
import { deriveSource } from "./source-derive"

// UTM / traffic-source capture. Mirrors the affiliate localStorage pattern
// (services/affiliates/referrals.js) but stays fully separate — see grilling
// affiliate code is left untouched, this uses its own keys.

const KEY_FIRST = "10A_src_first"
const KEY_LAST = "10A_src_last"
const KEY_GCLID = "10A_src_gclid"
const TTL_DAYS = Number(process.env.GATSBY_REFERRAL_TTL_DAYS) || 90

const ready = () => exists(window) && !!window.localStorage
// Same analytics-consent gate the affiliate tracker uses. No consent → no capture,
// no read. This is the source of the ~40% of records with blank source, by design.
const hasConsent = () =>
  typeof window.hasCookieConsent === "function" &&
  window.hasCookieConsent("analytics")

const now = () => new Date().getTime()

const write = (key, value) => {
  window.localStorage.setItem(key, value)
  window.localStorage.setItem(
    `${key}_expiry`,
    String(now() + TTL_DAYS * 24 * 60 * 60 * 1000),
  )
}

const read = key => {
  const exp = window.localStorage.getItem(`${key}_expiry`)
  if (exp && Number(exp) <= now()) {
    window.localStorage.removeItem(key)
    window.localStorage.removeItem(`${key}_expiry`)
    return ""
  }
  return window.localStorage.getItem(key) || ""
}

// Called on every route update. In a SPA document.referrer stays frozen at the
// value from the initial page load, so internal navigation naturally yields the
// same (or empty) referrer and deriveSource returns empty → stored values are
// left alone. That is why we don't need prevLocation to detect internal hops.
export const captureSource = () => {
  if (!ready() || !hasConsent()) return
  const siteHost = (window.location && window.location.host) || ""
  const referrer = exists(document) ? document.referrer : ""
  const { raw, gclid } = deriveSource(window.location.search, referrer, siteHost)

  if (raw) {
    write(KEY_LAST, raw)
    if (!read(KEY_FIRST)) write(KEY_FIRST, raw)
  }
  if (gclid) write(KEY_GCLID, gclid)
}

// Re-capture when consent is granted after initial page load. Without this,
// UTM params from the landing URL are lost because captureSource() bails on
// the first call (no consent yet) and SPA navigation clears the query string.
export const initSourceTracking = () => {
  if (!ready()) return
  window.addEventListener("cky.consent.updated", () => captureSource())
}

// Read for form submission. Returns raw strings to drop into hidden fields;
// classification into channel labels happens later in Zoho (grilling Q4/Q7).
// No consent gate on read — matches affiliate getActiveReferral() pattern.
// Data was consent-gated at write time; reading at submit is safe.
// Never throw: with site data blocked, merely touching window.localStorage
// raises SecurityError, and a lead is worth more than its attribution.
export const getSource = () => {
  const empty = { first: "", last: "", gclid: "" }
  try {
    if (!ready()) return empty
    return {
      first: read(KEY_FIRST),
      last: read(KEY_LAST),
      gclid: read(KEY_GCLID),
    }
  } catch (_) {
    return empty
  }
}
